import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { PublicQuoteView } from '@catalyst-psa/shared';

import { publicApi } from '../project-portal/publicApi.js';
import { QuoteDeliveryView } from './QuoteDeliveryView.js';

/**
 * Public customer-facing quote page (/q/:token) — unauthenticated, token is the only
 * credential. The document itself (sections, selectable lines, approval form) is the
 * shared {@link QuoteDeliveryView}, which internal preview modals reuse.
 */
export function PublicQuotePage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const query = useQuery({
    queryKey: ['public-quote', token],
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => publicApi.get<PublicQuoteView>(`/public/quotes/${token}`),
  });

  if (query.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p className="text-sm text-slate-500">Loading your quote…</p>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-slate-800">Quote unavailable</h1>
          <p className="text-sm text-slate-500">
            This quote link is invalid or has been disabled. Please contact your Catalyst
            representative for a new link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="mx-auto max-w-3xl px-4">
        <QuoteDeliveryView view={query.data} token={token} />
        <p className="mt-4 text-center text-xs text-slate-400">
          Questions about this quote? Reply to the email it arrived in and we&apos;ll help.
        </p>
      </div>
    </div>
  );
}
